import React, { useEffect } from 'react';
import { 
  Paper, 
  Typography, 
  Slider, 
  TextField, 
  Box, 
  InputAdornment,
  Button
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { VineyardParameters, ShadowInfo, TimeWindow } from '../types';

// Helper functions for cardinal directions
const getCardinalDirection = (angle: number): string => {
  const normalizedAngle = ((angle % 360) + 360) % 360;
  if (normalizedAngle >= 337.5 || normalizedAngle < 22.5) return 'N';
  if (normalizedAngle >= 22.5 && normalizedAngle < 67.5) return 'NE';
  if (normalizedAngle >= 67.5 && normalizedAngle < 112.5) return 'E';
  if (normalizedAngle >= 112.5 && normalizedAngle < 157.5) return 'SE';
  if (normalizedAngle >= 157.5 && normalizedAngle < 202.5) return 'S';
  if (normalizedAngle >= 202.5 && normalizedAngle < 247.5) return 'SW';
  if (normalizedAngle >= 247.5 && normalizedAngle < 292.5) return 'W';
  return 'NW';
};

const getOppositeCardinalDirection = (direction: string): string => {
  const opposites: { [key: string]: string } = {
    'N': 'S', 'NE': 'SW', 'E': 'W', 'SE': 'NW',
    'S': 'N', 'SW': 'NE', 'W': 'E', 'NW': 'SE'
  };
  return opposites[direction] || direction;
};

const formatCardinalDirectionPair = (direction: string): string => {
  return `${direction}-${getOppositeCardinalDirection(direction)}`;
};

interface ParameterPanelProps {
  parameters: VineyardParameters;
  selectedDate: Date;
  currentTime: Date;
  onParameterChange: (name: keyof VineyardParameters, value: number, visualUpdateOnly?: boolean) => void;
  onDateChange: (date: Date | null) => void;
  onNextDay: () => void;
  onPreviousDay: () => void;
  onTimeChange: (time: Date, isMoving?: boolean) => void;
  shadowInfo: ShadowInfo | null;
  optimalTimeWindows: TimeWindow[] | null;
  isLoadingOptimal?: boolean;
  isSliderMoving?: boolean;
}

const ParameterPanel: React.FC<ParameterPanelProps> = ({
  parameters,
  selectedDate,
  currentTime,
  onParameterChange,
  onDateChange,
  onNextDay,
  onPreviousDay,
  onTimeChange,
  shadowInfo,
  optimalTimeWindows,
  isLoadingOptimal = false,
  isSliderMoving = false
}) => {
  // Intermediate state for slider values while dragging
  const [localParameters, setLocalParameters] = React.useState<VineyardParameters>(parameters);
  // We'll use the isSliderMoving prop from the parent instead of maintaining our own state
  // const [isSliderMoving, setIsSliderMoving] = React.useState(false);

  // Update local parameters when props change
  React.useEffect(() => {
    setLocalParameters(parameters);
  }, [parameters]);

  // Handle slider movement (only update visuals)
  const handleSliderMove = (name: keyof VineyardParameters) => (_event: Event, value: number | number[]) => {
    if (typeof value === 'number') {
      // Update local state for immediate visual feedback
      setLocalParameters(prev => ({ ...prev, [name]: value }));
      
      // Only update visuals during sliding (shadow render, map overlay, etc.)
      // by passing a special flag to indicate this is just a visual update
      onParameterChange(name, value, true); // true = visualUpdateOnly
    }
  };

  // Handle slider release (update everything including status)
  const handleSliderChangeCommitted = (name: keyof VineyardParameters) => (_event: Event | React.SyntheticEvent, value: number | number[]) => {
    if (typeof value === 'number') {
      // Final update when slider is released (will trigger status recalculation)
      onParameterChange(name, value, false); // false = full update
    }
  };

  const handleInputChange = (name: keyof VineyardParameters) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(event.target.value);
    if (!isNaN(value)) {
      // Update both local state and trigger full update
      setLocalParameters(prev => ({ ...prev, [name]: value }));
      
      // Input changes are always treated as committed changes (explicit visualUpdateOnly=false)
      onParameterChange(name, value, false);
    }
  };

  // Convert time to slider value (0-24 hours)
  const timeToSliderValue = (time: Date): number => {
    return time.getHours() + time.getMinutes() / 60;
  };

  // Convert slider value to time
  const sliderValueToTime = (value: number): Date => {
    const hours = Math.floor(value);
    const minutes = Math.round((value - hours) * 60);
    const newTime = new Date(currentTime);
    newTime.setHours(hours, minutes, 0, 0);
    return newTime;
  };

  // Handle time slider movement (continuous updates during sliding)
  const handleTimeMove = (_event: Event, value: number | number[]) => {
    if (typeof value === 'number') {
      onTimeChange(sliderValueToTime(value), true); // Pass true to indicate slider is moving
    }
  };

  // Handle time slider release (final update when slider is released)
  const handleTimeChangeCommitted = (_event: Event | React.SyntheticEvent, value: number | number[]) => {
    if (typeof value === 'number') {
      // Trigger time change with a flag that indicates this is a committed change
      const newTime = sliderValueToTime(value);
      onTimeChange(newTime, false); // Pass false to indicate slider is released
      
      // Also trigger a parameter change to ensure optimal windows get updated
      // Using row_direction as a dummy parameter that won't actually change
      onParameterChange('row_direction', parameters.row_direction, false);
    }
  };

  // Debug logging
  useEffect(() => {
    if (optimalTimeWindows && optimalTimeWindows.length > 0) {
      console.log("Current time:", currentTime);
      console.log("Optimal windows:", optimalTimeWindows);
      
      optimalTimeWindows.forEach((window, index) => {
        console.log(`Window ${index+1}:`, {
          raw: `${window.start} - ${window.end}`,
          duration: window.duration_minutes || 0,
          timezone: window.timezone || "Unknown"
        });
      });
    }
  }, [optimalTimeWindows, currentTime]);

  return (
    <Paper elevation={3} sx={{ 
      p: 2, 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      background: 'linear-gradient(to bottom, #ffffff, #f9fafb)',
      borderRadius: 2,
      overflowY: 'auto'
    }}>
      <Typography variant="h6" gutterBottom sx={{ 
        fontWeight: 600, 
        color: '#1a365d',
        borderBottom: '1px solid #e2e8f0',
        pb: 1
      }}>
        Vineyard Parameters
      </Typography>
      
      {/* Row Direction */}
      <Box sx={{ mb: 3 }}>
        <Typography id="row-direction-slider" gutterBottom sx={{ 
          fontWeight: 500,
          display: 'flex',
          alignItems: 'center'
        }}>
          <span style={{ 
            display: 'inline-block', 
            width: '16px', 
            height: '16px',
            marginRight: '8px',
            background: 'linear-gradient(to right, #4299e1, #3182ce)',
            borderRadius: '4px'
          }}></span>
          Row Direction: {localParameters.row_direction}° ({formatCardinalDirectionPair(getCardinalDirection(localParameters.row_direction))})
        </Typography>
        <Slider
          aria-labelledby="row-direction-slider"
          value={localParameters.row_direction}
          min={-180}
          max={180}
          step={1}
          onChange={handleSliderMove('row_direction')}
          onChangeCommitted={handleSliderChangeCommitted('row_direction')}
          valueLabelDisplay="auto"
          marks={[
            { value: -180, label: 'S' },
            { value: -90, label: 'W' },
            { value: 0, label: 'N' },
            { value: 90, label: 'E' },
            { value: 180, label: 'S' },
          ]}
          sx={{
            '& .MuiSlider-thumb': {
              backgroundColor: '#3182CE',
              '&:hover, &.Mui-focusVisible': {
                boxShadow: '0 0 0 8px rgba(49, 130, 206, 0.16)',
              }
            },
            '& .MuiSlider-track': {
              backgroundColor: '#3182CE',
            },
            '& .MuiSlider-rail': {
              backgroundColor: '#E2E8F0',
            },
            '& .MuiSlider-mark': {
              backgroundColor: '#CBD5E0',
              height: 8,
              width: 2,
              marginTop: -3
            },
            '& .MuiSlider-markLabel': {
              fontSize: '0.75rem',
              color: '#4A5568',
              fontWeight: 500
            }
          }}
        />
      </Box>
      
      {/* Plant Height */}
      <Box sx={{ mb: 3 }}>
        <Typography id="plant-height-slider" gutterBottom sx={{ fontWeight: 500, display: 'flex', alignItems: 'center' }}>
          <span style={{ 
            display: 'inline-block', 
            width: '16px', 
            height: '16px',
            marginRight: '8px',
            background: 'linear-gradient(to right, #38A169, #2F855A)',
            borderRadius: '4px'
          }}></span>
          Plant Height
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Slider
            aria-labelledby="plant-height-slider"
            value={localParameters.plant_height}
            min={0}
            max={300}
            step={10}
            onChange={handleSliderMove('plant_height')}
            onChangeCommitted={handleSliderChangeCommitted('plant_height')}
            sx={{ 
              flex: 1, 
              mr: 2,
              '& .MuiSlider-thumb': {
                backgroundColor: '#38A169',
                '&:hover, &.Mui-focusVisible': {
                  boxShadow: '0 0 0 8px rgba(56, 161, 105, 0.16)',
                }
              },
              '& .MuiSlider-track': {
                backgroundColor: '#38A169',
              },
              '& .MuiSlider-rail': {
                backgroundColor: '#E2E8F0',
              }
            }}
          />
          <TextField
            value={localParameters.plant_height}
            onChange={handleInputChange('plant_height')}
            InputProps={{
              endAdornment: <InputAdornment position="end">cm</InputAdornment>,
              inputProps: { min: 0, max: 300 }
            }}
            size="small"
            type="number"
            sx={{ 
              width: 100,
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: '#CBD5E0',
                },
                '&:hover fieldset': {
                  borderColor: '#38A169',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#38A169',
                },
              }
            }}
          />
        </Box>
      </Box>
      
      {/* Row Distance */}
      <Box sx={{ mb: 3 }}>
        <Typography id="row-distance-slider" gutterBottom sx={{ fontWeight: 500, display: 'flex', alignItems: 'center' }}>
          <span style={{ 
            display: 'inline-block', 
            width: '16px', 
            height: '16px',
            marginRight: '8px',
            background: 'linear-gradient(to right, #805AD5, #6B46C1)',
            borderRadius: '4px'
          }}></span>
          Inter-row Distance
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Slider
            aria-labelledby="row-distance-slider"
            value={localParameters.row_distance}
            min={100}
            max={500}
            step={10}
            onChange={handleSliderMove('row_distance')}
            onChangeCommitted={handleSliderChangeCommitted('row_distance')}
            sx={{ 
              flex: 1, 
              mr: 2,
              '& .MuiSlider-thumb': {
                backgroundColor: '#805AD5',
                '&:hover, &.Mui-focusVisible': {
                  boxShadow: '0 0 0 8px rgba(128, 90, 213, 0.16)',
                }
              },
              '& .MuiSlider-track': {
                backgroundColor: '#805AD5',
              },
              '& .MuiSlider-rail': {
                backgroundColor: '#E2E8F0',
              }
            }}
          />
          <TextField
            value={localParameters.row_distance}
            onChange={handleInputChange('row_distance')}
            InputProps={{
              endAdornment: <InputAdornment position="end">cm</InputAdornment>,
              inputProps: { min: 100, max: 500 }
            }}
            size="small"
            type="number"
            sx={{ 
              width: 100,
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: '#CBD5E0',
                },
                '&:hover fieldset': {
                  borderColor: '#805AD5',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#805AD5',
                },
              }
            }}
          />
        </Box>
      </Box>
      
      {/* Date and Time */}
      <Box sx={{ 
        mb: 3, 
        p: 2, 
        backgroundColor: '#EBF8FF', 
        borderRadius: 1,
        border: '1px solid #BEE3F8'
      }}>
        <Typography gutterBottom sx={{ 
          fontWeight: 600, 
          color: '#2C5282',
          display: 'flex',
          alignItems: 'center'
        }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2C5282" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px' }}>
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
            <line x1="16" y1="2" x2="16" y2="6"></line>
            <line x1="8" y1="2" x2="8" y2="6"></line>
            <line x1="3" y1="10" x2="21" y2="10"></line>
          </svg>
          Date and Time
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              value={selectedDate}
              onChange={onDateChange}
              slotProps={{ 
                textField: { 
                  size: 'small', 
                  fullWidth: true,
                  sx: {
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'white',
                      '& fieldset': {
                        borderColor: '#CBD5E0',
                      },
                      '&:hover fieldset': {
                        borderColor: '#3182CE',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#3182CE',
                      },
                    }
                  }
                } 
              }}
            />
          </LocalizationProvider>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Button 
            variant="outlined" 
            size="small" 
            onClick={onPreviousDay}
            sx={{
              borderColor: '#3182CE',
              color: '#3182CE',
              '&:hover': {
                borderColor: '#2B6CB0',
                backgroundColor: 'rgba(49, 130, 206, 0.04)',
              }
            }}
          >
            Previous Week
          </Button>
          <Button 
            variant="outlined" 
            size="small" 
            onClick={onNextDay}
            sx={{
              borderColor: '#3182CE',
              color: '#3182CE',
              '&:hover': {
                borderColor: '#2B6CB0',
                backgroundColor: 'rgba(49, 130, 206, 0.04)',
              }
            }}
          >
            Next Week
          </Button>
        </Box>
      </Box>
      
      {/* Constraints */}
      <Typography variant="subtitle1" gutterBottom sx={{ 
        fontWeight: 600, 
        color: '#1A365D',
        display: 'flex',
        alignItems: 'center',
        mt: 1
      }}>
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1A365D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px' }}>
          <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
        </svg>
        Constraints
      </Typography>
      
      {/* Min Angle from Row */}
      <Box sx={{ mb: 3 }}>
        <Typography id="min-angle-slider" gutterBottom sx={{ fontWeight: 500, display: 'flex', alignItems: 'center' }}>
          <span style={{ 
            display: 'inline-block', 
            width: '16px', 
            height: '16px',
            marginRight: '8px',
            background: 'linear-gradient(to right, #ED8936, #DD6B20)',
            borderRadius: '4px'
          }}></span>
          Minimum Angle from Row
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Slider
            aria-labelledby="min-angle-slider"
            value={localParameters.min_angle_from_row}
            min={0}
            max={90}
            step={1}
            onChange={handleSliderMove('min_angle_from_row')}
            onChangeCommitted={handleSliderChangeCommitted('min_angle_from_row')}
            sx={{ 
              flex: 1, 
              mr: 2,
              '& .MuiSlider-thumb': {
                backgroundColor: '#ED8936',
                '&:hover, &.Mui-focusVisible': {
                  boxShadow: '0 0 0 8px rgba(237, 137, 54, 0.16)',
                }
              },
              '& .MuiSlider-track': {
                backgroundColor: '#ED8936',
              },
              '& .MuiSlider-rail': {
                backgroundColor: '#E2E8F0',
              }
            }}
          />
          <TextField
            value={localParameters.min_angle_from_row}
            onChange={handleInputChange('min_angle_from_row')}
            InputProps={{
              endAdornment: <InputAdornment position="end">°</InputAdornment>,
              inputProps: { min: 0, max: 90 }
            }}
            size="small"
            type="number"
            sx={{ 
              width: 100,
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: '#CBD5E0',
                },
                '&:hover fieldset': {
                  borderColor: '#ED8936',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#ED8936',
                },
              }
            }}
          />
        </Box>
      </Box>
      
      {/* Min Distance from Row */}
      <Box sx={{ mb: 3 }}>
        <Typography id="min-distance-slider" gutterBottom sx={{ fontWeight: 500, display: 'flex', alignItems: 'center' }}>
          <span style={{ 
            display: 'inline-block', 
            width: '16px', 
            height: '16px',
            marginRight: '8px',
            background: 'linear-gradient(to right, #E53E3E, #C53030)',
            borderRadius: '4px'
          }}></span>
          Minimum Distance from Next Row
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Slider
            aria-labelledby="min-distance-slider"
            value={localParameters.min_distance_from_row}
            min={0}
            max={100}
            step={5}
            onChange={handleSliderMove('min_distance_from_row')}
            onChangeCommitted={handleSliderChangeCommitted('min_distance_from_row')}
            sx={{ 
              flex: 1, 
              mr: 2,
              '& .MuiSlider-thumb': {
                backgroundColor: '#E53E3E',
                '&:hover, &.Mui-focusVisible': {
                  boxShadow: '0 0 0 8px rgba(229, 62, 62, 0.16)',
                }
              },
              '& .MuiSlider-track': {
                backgroundColor: '#E53E3E',
              },
              '& .MuiSlider-rail': {
                backgroundColor: '#E2E8F0',
              }
            }}
          />
          <TextField
            value={localParameters.min_distance_from_row}
            onChange={handleInputChange('min_distance_from_row')}
            InputProps={{
              endAdornment: <InputAdornment position="end">cm</InputAdornment>,
              inputProps: { min: 0, max: 100 }
            }}
            size="small"
            type="number"
            sx={{ 
              width: 100,
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: '#CBD5E0',
                },
                '&:hover fieldset': {
                  borderColor: '#E53E3E',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#E53E3E',
                },
              }
            }}
          />
        </Box>
      </Box>
      
      {/* Optimal Time Windows */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom sx={{ 
          fontWeight: 600, 
          color: '#276749',
          display: 'flex',
          alignItems: 'center'
        }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#276749" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px' }}>
            <circle cx="12" cy="12" r="10"></circle>
            <polyline points="12 6 12 12 16 14"></polyline>
          </svg>
          Optimal Time Windows
          {isLoadingOptimal && !isSliderMoving && (
            <Typography 
              component="span" 
              sx={{ 
                ml: 1, 
                fontSize: '0.8rem', 
                fontStyle: 'italic',
                color: 'text.secondary',
                verticalAlign: 'middle'
              }}
            >
              (Recalculating...)
            </Typography>
          )}
        </Typography>
        
        {/* Show optimal windows or no optimal times message */}
        {optimalTimeWindows && optimalTimeWindows.length > 0 ? (
          <>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 1.5, 
                bgcolor: 'success.light', 
                color: 'success.contrastText', 
                borderRadius: 1,
                backgroundImage: 'linear-gradient(to right, #38A169, #2F855A)',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
              }}
            >
              {optimalTimeWindows.map((window, index) => {
                // Get duration for display
                const durationMinutes = window.duration_minutes || 0;
                const hours = Math.floor(durationMinutes / 60);
                const minutes = durationMinutes % 60;
                
                // Check if current time is within this window
                let isCurrentTimeInWindow = false;
                try {
                  const startTime = new Date(window.start);
                  const endTime = new Date(window.end);
                  isCurrentTimeInWindow = 
                    currentTime.getTime() >= startTime.getTime() && 
                    currentTime.getTime() <= endTime.getTime();
                } catch (e) {
                  console.error('Error comparing times:', e);
                }
                
                return (
                  <Box 
                    key={index} 
                    sx={{ 
                      mb: index < optimalTimeWindows.length - 1 ? 1 : 0,
                      position: 'relative',
                      borderLeft: isCurrentTimeInWindow ? '4px solid #fff' : 'none',
                      pl: isCurrentTimeInWindow ? 1 : 0,
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: isCurrentTimeInWindow ? '0 0 0 2px rgba(255,255,255,0.5)' : 'none',
                      }
                    }}
                  >
                    <Typography variant="body1" fontWeight="bold">
                      {window.start}
                      {' - '}
                      {window.end}
                      {window.timezone && (
                        <Typography 
                          component="span" 
                          sx={{ 
                            ml: 1, 
                            color: 'rgba(255,255,255,0.7)',
                            fontSize: '0.75rem',
                            fontStyle: 'italic'
                          }}
                        >
                          ({window.timezone.replace('Etc/', '')})
                        </Typography>
                      )}
                      {isCurrentTimeInWindow && !isSliderMoving && (
                        <Typography 
                          component="span" 
                          sx={{ 
                            ml: 1, 
                            bgcolor: '#fff', 
                            color: 'success.dark',
                            px: 0.5,
                            py: 0.1,
                            borderRadius: 0.5,
                            fontSize: '0.75rem',
                            fontWeight: 'bold',
                            verticalAlign: 'middle'
                          }}
                        >
                          CURRENT
                        </Typography>
                      )}
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block' }}>
                      Duration: {hours > 0 ? `${hours}h ` : ''}{minutes}m
                    </Typography>
                    {index < optimalTimeWindows.length - 1 && <Box sx={{ height: 1, bgcolor: 'rgba(255,255,255,0.2)', mt: 1 }} />}
                  </Box>
                );
              })}
            </Paper>
            <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
              These are times when shadows won't interfere with adjacent rows based on your constraints.
            </Typography>
          </>
        ) : (
          // Show no optimal times message
          <Box sx={{ 
            p: 2, 
            bgcolor: 'rgba(229, 62, 62, 0.1)', 
            borderRadius: 1, 
            border: '1px dashed #E53E3E',
            display: 'flex',
            alignItems: 'center'
          }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#E53E3E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '12px', flexShrink: 0 }}>
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            <Typography variant="body2" color="#C53030">
              No times today meet all shadow constraints. Try adjusting the minimum angle or distance requirements.
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default ParameterPanel; 